import { format, isSameDay, isSameMonth, isSaturday, isSunday } from "date-fns";
import * as locales from "date-fns/locale";
import { useEffect, useState } from "react";
import styles from "./date-picker-range.module.css";
import cn from "classnames";
import { ChevronLeft, ChevronRight } from "frontend/icons/chevron";
import { DPDay, useDatePicker } from "@rehookify/datepicker";

function getLocale() {
  const locale = navigator.language || "en-US";
  const normalizedLocale = locale.split("-").join("");

  if (Object.prototype.hasOwnProperty.call(locales, normalizedLocale)) {
    return (locales as any)[normalizedLocale];
  }
  return locales.enUS;
}

const themeVars: Record<string, React.CSSProperties> = {
  light: {
    "--dp-text": "#113357",
    "--dp-accent": "#1973FF",
    "--dp-accent-text": "#ffffff",
    "--dp-range-bg": "#E6F0FF",
    "--dp-range-text": "#113357",
    "--dp-hover-bg": "#eff3f8",
    "--dp-border": "#f0f0f0",
    "--dp-secondary-text": "#c8c8c8",
    "--dp-icon-color": "#1973FF",
  } as React.CSSProperties,
  dark: {
    "--dp-text": "#ffffff",
    "--dp-accent": "#599AFF",
    "--dp-accent-text": "#ffffff",
    "--dp-range-bg": "rgba(25, 115, 255, 0.3)",
    "--dp-range-text": "#ffffff",
    "--dp-hover-bg": "rgba(255, 255, 255, 0.2)",
    "--dp-border": "#f0f0f0",
    "--dp-secondary-text": "#c8c8c8",
    "--dp-icon-color": "#599AFF",
  } as React.CSSProperties,
  cim: {
    "--dp-text": "#fff",
    "--dp-accent": "#fff",
    "--dp-accent-text": "#000",
    "--dp-range-bg": "#313131",
    "--dp-range-text": "#fff",
    "--dp-hover-bg": "rgba(255, 255, 255, 0.2)",
    "--dp-border": "#454545",
    "--dp-secondary-text": "#c8c8c8",
    "--dp-icon-color": "#83868F",
    "--dp-margin-top": "6px",
  } as React.CSSProperties,
};

export function DatePickerRange({
  fromDate,
  toDate,
  onSelected,
  theme = "light",
  showActions = "none",
  onClear,
  onApply,
}: {
  fromDate?: Date;
  toDate?: Date;
  onSelected?: (fromDate: Date, toDate?: Date) => void;
  theme?: "light" | "dark" | "cim";
  showActions?: "clear" | "apply" | "both" | "none";
  onClear?: () => void;
  onApply?: (fromDate?: Date, toDate?: Date) => void;
}) {
  const currentThemeVars = themeVars[theme] || themeVars.light;

  const [selectedDates, onDatesChange] = useState<Date[]>([fromDate, toDate].filter((d) => d !== undefined) as Date[]);
  const [offsetDate, onOffsetChange] = useState<Date>(new Date());

  const {
    data: { weekDays, calendars, months },
    propGetters: { dayButton, addOffset, subtractOffset },
  } = useDatePicker({
    selectedDates,
    onDatesChange,
    offsetDate,
    onOffsetChange,
    dates: { toggle: true, mode: "range" },
  });
  const locale = getLocale();
  const { days } = calendars[0];

  const showClear = showActions === "clear" || showActions === "both";
  const showApply = showActions === "apply" || showActions === "both";
  const [start, end] = selectedDates;

  const curerntMonth = months.find((m) => m.active)?.$date || new Date();

  useEffect(() => {
    onSelected?.(selectedDates[0], selectedDates[1]);
  }, [selectedDates]);

  return (
    <div className={styles.container} style={currentThemeVars}>
      <div className={styles.header}>
        <button
          {...subtractOffset({
            months: 1,
          })}
        >
          <ChevronLeft color="var(--dp-icon-color)" />
        </button>
        {format(curerntMonth, "LLLL yyyy", { locale })}
        <button
          {...addOffset({
            months: 1,
          })}
        >
          <ChevronRight color="var(--dp-icon-color)" />
        </button>
      </div>

      <div className={styles.hr} />

      <div className={styles.calendar}>
        {weekDays.map((name, index) => (
          <div key={index} className={styles.dayName}>
            {name}
          </div>
        ))}
        {days.map((day: DPDay) => (
          <button
            key={day.$date.toDateString()}
            className={cn(styles.day, {
              [styles.isSunday]: isSunday(day.$date),
              [styles.isSaturday]: isSaturday(day.$date),

              [styles.isOutOfCurrentMonth]: !isSameMonth(day.$date, curerntMonth),
              [styles.inRange]: start && end && day.$date > start && day.$date < end,
              [styles.selected]: (start && isSameDay(day.$date, start)) || (end && isSameDay(day.$date, end)),
              [styles.rangeStart]: start && isSameDay(day.$date, start) && !!end,
              [styles.rangeEnd]: end && isSameDay(day.$date, end),
              [styles.startAndEndDate]: start && isSameDay(day.$date, start) && end && isSameDay(day.$date, end),
            })}
            data-date={day.$date.toISOString()}
            {...dayButton(day)}
          >
            {format(day.$date, "d", { locale })}
          </button>
        ))}
      </div>
      <div className={styles.calendarActions}>
        {showClear && (
          <button
            className={styles.calendarAction}
            onClick={() => {
              onDatesChange([]);
              onClear?.();
            }}
          >
            Clear
          </button>
        )}
        {showApply && (
          <button
            className={styles.calendarAction}
            onClick={() => {
              onApply?.(start, end);
            }}
          >
            Apply
          </button>
        )}
      </div>
    </div>
  );
}
